//SearchBar.tsx:
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSearch } from "Context/SearchContext";
import { useEcomConfig } from "hooks/useEcomConfig";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";
import { useGetCategoriesByProductTypeQuery } from "Services/CategoryApiSlice";
import { useGetActiveAuthorsByProductTypeQuery } from "Services/Admin/ActiveAuthorApiSlice";
import { useGetActiveLanguagesByProductTypeQuery } from "Services/Admin/ActiveLanguageApiSlice";
import { useGetAllActiveAcademicsQuery } from "Services/Admin/ActiveAcademicApiSlice";
import { useGetAllActiveMerchandisesQuery } from "Services/Admin/ActiveMerchandiseApiSlice";
import { encodeParam, decodeParamList, encodeParamList } from "Utilities/urlEncoder";
import { useLazyGetListOfBookProductsWithFilterQuery } from "Services/ProductApiSlice";

interface Suggestion {
  label: string;
  type: "CATEGORY" | "AUTHOR" | "LANGUAGE" | "ACADEMIC" | "MERCHANDISE" | "RECORD" | "BOOK";
  code?: string; // category code for navigation
}

const MAX_SUGGESTIONS = 8;

const SearchBar: React.FC = () => {
  const { showAuthor, showLanguage, showAcademic, showMerchandise } = useEcomConfig();
  const { searchTerm, setSearchTerm } = useSearch();
  const { queueAnalytics, resolveAnalytics } = useSearchAnalytics();
  const [inputValue, setInputValue] = useState(searchTerm);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeProductType = useMemo(() => {
    const typeFromParam = searchParams.get("productType");
    if (typeFromParam) {
      return decodeParamList(typeFromParam).map(v => v.toUpperCase()).join(",");
    }

    // Fallback for legacy paths
    if (location.pathname.includes("/record-listing")) return "RECORD";
    if (location.pathname.includes("/merchandise-listing")) return "MERCHANDISE";
    
    return "BOOK";
  }, [location.pathname, searchParams]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoggedRef = useRef<string>("");
  const lastSearchQueryRef = useRef<string>("");

  // Fetch all available data for suggestions
  const { data: categories = [], isLoading: isCategoriesLoading } = useGetCategoriesByProductTypeQuery("BOOK");
  const { data: authors = [], isLoading: isAuthorsLoading } = useGetActiveAuthorsByProductTypeQuery("BOOK");
  const { data: languages = [], isLoading: isLanguagesLoading } = useGetActiveLanguagesByProductTypeQuery("BOOK");
  const { data: academics = [], isLoading: isAcademicsLoading } = useGetAllActiveAcademicsQuery();
  const { data: merchandises = [], isLoading: isMerchandisesLoading } = useGetAllActiveMerchandisesQuery();

  const [triggerBookSearch, { data: bookSearchData, isLoading: isBookLoading, isFetching: isBookFetching, isError: isBookError }] = useLazyGetListOfBookProductsWithFilterQuery();

  const [isSearching, setIsSearching] = useState(false);

  const placeholderText = useMemo(() => {
    if (!showAuthor) {
      return "Search by ISBN or Book Name";
    }
    return "Search by ISBN, Book Name, Author Name, Merchandise, or Record";
  }, [showAuthor]);

  const isCatalogLoading = 
    isCategoriesLoading ||
    (showAuthor && isAuthorsLoading) ||
    (showLanguage && isLanguagesLoading) ||
    (showAcademic && isAcademicsLoading) ||
    (showMerchandise && isMerchandisesLoading);

  // Debounced search trigger for autocomplete suggestions (200ms delay for premium responsiveness)
  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const delayDebounceFn = setTimeout(async () => {
      lastSearchQueryRef.current = trimmed;
      try {
        await triggerBookSearch({
          mode: "CRITERIA",
          page: 1,
          productsCount: 30, // Request larger result pool to avoid missing matching books
          search: trimmed,
          productType: "BOOK,RECORD,MERCHANDISE",
        }).unwrap();
      } catch (err) {
        console.error("Book search failed:", err);
      } finally {
        if (lastSearchQueryRef.current === trimmed) {
          setIsSearching(false);
        }
      }
    }, 200);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValue, triggerBookSearch, activeProductType]);

  // Build suggestions based on inputValue
  const suggestions = useMemo((): Suggestion[] => {
    const trimmed = inputValue.trim().toLowerCase();
    if (!trimmed) return [];

    const results: Suggestion[] = [];

    // Categories
    categories.forEach((cat) => {
      if (cat.categoryName.toLowerCase().includes(trimmed)) {
        results.push({
          label: cat.categoryName,
          type: "CATEGORY",
          code: cat.categoryId,
        });
      }
    });

    // Authors
    if (showAuthor) {
      authors.forEach((a) => {
        if (a.author.toLowerCase().includes(trimmed) && a.isActive === "1") {
          results.push({ label: a.author, type: "AUTHOR" });
        }
      });
    }

    // Languages
    if (showLanguage) {
      languages.forEach((l) => {
        if (l.language.toLowerCase().includes(trimmed) && l.isActive === "1") {
          results.push({ label: l.language, type: "LANGUAGE" });
        }
      });
    }

    // Academics
    if (showAcademic) {
      academics.forEach((ac) => {
        if (ac.academic.toLowerCase().includes(trimmed) && ac.isActive === "1") {
          results.push({ label: ac.academic, type: "ACADEMIC" });
        }
      });
    }

    // Merchandises
    if (showMerchandise) {
      merchandises.forEach((m) => {
        if (m.merchandise.toLowerCase().includes(trimmed) && m.isActive === "1") {
          results.push({ label: m.merchandise, type: "MERCHANDISE" });
        }
      });
    }

    // Books
    if (bookSearchData) {
      const products = (bookSearchData as any).results?.products || (bookSearchData as any).products || [];
      products.forEach((p: any) => {
        const name = p.product?.itemDesc || p.product?.title || p.product?.item;
        if (name) {
          const pt = (p.product?.productType || p.product?.productTypeId || "BOOK").toUpperCase();
          const targetType = (pt === "RECORD" || pt === "MERCHANDISE" || pt === "BOOK") ? pt : "BOOK";
          if (!results.some((r) => r.label === name && (r.type === "BOOK" || r.type === "RECORD" || r.type === "MERCHANDISE"))) {
            results.push({
              label: name,
              type: targetType as any,
              code: p.product?.item,
            });
          }
        }
      });
    }

    // Score weight for suggestion matching (prioritize exact/word prefix matches like Amazon)
    const getMatchScore = (label: string, query: string) => {
      const lbl = label.toLowerCase();
      const q = query.toLowerCase();
      if (lbl.startsWith(q)) {
        return 3; // Starts with query
      }
      const words = lbl.split(/[\s\-:_]+/);
      if (words.some((word) => word.startsWith(q))) {
        return 2; // Word starts with query
      }
      if (lbl.includes(q)) {
        return 1; // Substring match
      }
      return 0;
    };

    // Sort results based on score weight and type priorities
    const rankedResults = [...results].sort((a, b) => {
      const scoreA = getMatchScore(a.label, trimmed);
      const scoreB = getMatchScore(b.label, trimmed);
      if (scoreB !== scoreA) {
        return scoreB - scoreA;
      }
      // Prioritize Books/Records/Merchandise and Categories if score is equal
      const priority = (type: string) => {
        if (type === "BOOK" || type === "RECORD" || type === "MERCHANDISE") return 2;
        if (type === "CATEGORY") return 1;
        return 0;
      };
      const prioA = priority(a.type);
      const prioB = priority(b.type);
      if (prioB !== prioA) {
        return prioB - prioA;
      }
      return a.label.localeCompare(b.label);
    });

    return rankedResults.slice(0, MAX_SUGGESTIONS);
  }, [inputValue, categories, authors, languages, academics, merchandises, showAuthor, showLanguage, showAcademic, showMerchandise, bookSearchData]);

  const uiState = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return "idle";

    // If catalog metadata is still loading, we are loading
    if (isCatalogLoading) return "loading";

    // If we are debouncing or fetching the API, we are loading
    if (trimmed.length >= 2 && (isSearching || isBookLoading || isBookFetching)) {
      return "loading";
    }

    // If book API returned an error, we can either show error or fallback
    if (trimmed.length >= 2 && isBookError) {
      return "error";
    }

    // If suggestions list has items, it's a success
    if (suggestions.length > 0) {
      return "success";
    }

    // If query length is >= 2, catalog is loaded, and suggestions list is empty, it's empty
    if (trimmed.length >= 2) {
      return "empty";
    }

    // Default to loading for first keystroke (length 1) when no local matches exist yet
    return "loading";
  }, [
    inputValue,
    isCatalogLoading,
    isSearching,
    isBookLoading,
    isBookFetching,
    isBookError,
    suggestions.length,
  ]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (searchTerm !== inputValue) {
      setInputValue(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get("search");

    // If there is a search term in the URL, sync it to state
    if (urlSearch) {
      const decodedSearch = decodeURIComponent(urlSearch);
      if (decodedSearch !== searchTerm) {
        setSearchTerm(decodedSearch);
        setInputValue(decodedSearch);
      }
    } else {
      // If no search in URL and we are on the listing page, clear it
      if (location.pathname.includes("/books-listing") || location.pathname.includes("/product-listing")) {
        if (searchTerm !== "" || inputValue !== "") {
          setSearchTerm("");
          setInputValue("");
        }
      } else {
        const isSearchContext =
          location.pathname.includes("/books-listing") ||
          location.pathname.includes("/product-listing") ||
          location.pathname.includes("/product") ||
          location.pathname.includes("/s"); // s is search view

        if (!isSearchContext || location.pathname === "/") {
          if (searchTerm !== "" || inputValue !== "") {
            setSearchTerm("");
            setInputValue("");
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Navigate to the correct listing page when a suggestion is selected
  const handleSuggestionSelect = (suggestion: Suggestion) => {
    queueAnalytics(suggestion.type, suggestion.label, ((suggestion.type === "BOOK" || suggestion.type === "RECORD" || suggestion.type === "MERCHANDISE") && suggestion.code) ? suggestion.code : null);
    setShowSuggestions(false);
    setInputValue(suggestion.label);
    // Clear any pending unsuccessful log since user selected a valid result
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    if ((suggestion.type === "BOOK" || suggestion.type === "RECORD" || suggestion.type === "MERCHANDISE") && suggestion.code) {
      resolveAnalytics(true);
      navigate(`/product?pid=${encodeParam(suggestion.code || "")}`);
      return;
    }

    const currentProductType = searchParams.get("productType");
    const ptQuery = currentProductType ? `productType=${currentProductType}&` : "";

    switch (suggestion.type) {
      case "CATEGORY":
        navigate(`/product-listing?${ptQuery}category=${encodeParam(suggestion.code || "")}`);
        break;
      case "AUTHOR":
        navigate(`/product-listing?${ptQuery}author=${encodeParam(suggestion.label)}`);
        break;
      case "LANGUAGE":
        navigate(`/product-listing?${ptQuery}language=${encodeParam(suggestion.label)}`);
        break;
      case "ACADEMIC":
        navigate(`/product-listing?${ptQuery}academic=${encodeParam(suggestion.label)}`);
        break;
      case "MERCHANDISE":
        navigate(`/product-listing?${ptQuery}merchandise=${encodeParam(suggestion.label)}`);
        break;
      default:
        break;
    }
  };

  // Full-text search submit (existing behavior preserved)
  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      setShowSuggestions(false);
      // Clear any pending unsuccessful log since user is doing a full search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      const params = new URLSearchParams(location.search);
      const urlSearch = params.get("search") || "";
      if (
        trimmed.toLowerCase() !== urlSearch.toLowerCase() &&
        trimmed.toLowerCase() !== searchTerm.toLowerCase() &&
        trimmed.toLowerCase() !== lastLoggedRef.current.toLowerCase()
      ) {
        queueAnalytics("SEARCH_BAR", trimmed);
        lastLoggedRef.current = trimmed;
      }
      setSearchTerm(trimmed);
      const currentProductType = searchParams.get("productType");
      if (currentProductType) {
        navigate(
          `/product-listing?productType=${currentProductType}&search=${encodeURIComponent(trimmed)}`,
        );
      } else {
        navigate(
          `/product-listing?search=${encodeURIComponent(trimmed)}`,
        );
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        return;
      }
      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        handleSuggestionSelect(suggestions[activeIndex]);
        return;
      }
      if (e.key === "Escape") {
        setShowSuggestions(false);
        setActiveIndex(-1);
        return;
      }
    }
    if (e.key === "Enter") handleSubmit();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setActiveIndex(-1);

    if (!val.trim()) {
      setShowSuggestions(false);
      setSearchTerm("");
      lastLoggedRef.current = "";
      setIsSearching(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      const params = new URLSearchParams(location.search);
      if (params.has("search")) {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`);
      }
      return;
    }

    setShowSuggestions(true);

    // Debounced unsuccessful search logging — same pattern as CategoryDropdown
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const trimmed = val.trim();
      const lower = trimmed.toLowerCase();
      let hasAnyMatch = false;

      // Check categories
      if (categories.some((cat) => cat.categoryName.toLowerCase().includes(lower))) {
        hasAnyMatch = true;
      }
      // Check authors
      if (!hasAnyMatch && showAuthor && authors.some((a) => a.isActive === "1" && a.author.toLowerCase().includes(lower))) {
        hasAnyMatch = true;
      }
      // Check languages
      if (!hasAnyMatch && showLanguage && languages.some((l) => l.isActive === "1" && l.language.toLowerCase().includes(lower))) {
        hasAnyMatch = true;
      }
      // Check academics
      if (!hasAnyMatch && showAcademic && academics.some((ac) => ac.isActive === "1" && ac.academic.toLowerCase().includes(lower))) {
        hasAnyMatch = true;
      }
      // Check merchandises
      if (!hasAnyMatch && showMerchandise && merchandises.some((m) => m.isActive === "1" && m.merchandise.toLowerCase().includes(lower))) {
        hasAnyMatch = true;
      }

      let apiFailed = false;
      if (trimmed.length > 1) {
        try {
          let products = (bookSearchData as any)?.results?.products || (bookSearchData as any)?.products || [];
          if (products.length === 0 || lastSearchQueryRef.current.toLowerCase() !== lower) {
            const bookData = await triggerBookSearch({
              mode: "CRITERIA",
              page: 1,
              productsCount: 30,
              search: trimmed,
              productType: "BOOK,RECORD,MERCHANDISE",
            }).unwrap();
            products = (bookData as any)?.results?.products || (bookData as any)?.products || [];
          }
          const matchesBook = products.some((p: any) => {
            const name = p.product?.itemDesc || p.product?.title || p.product?.item || "";
            return name.toLowerCase().includes(lower);
          });
          if (matchesBook) {
            hasAnyMatch = true;
          }
        } catch (error) {
          console.error("Book search error in analytics:", error);
          apiFailed = true;
        }
      }

      if (apiFailed) {
        return;
      }

      if (!hasAnyMatch) {
        const lastLogged = lastLoggedRef.current;
        const searchType = "SEARCH_BAR";

        // Prefix replacement: if user extends a previously logged term, replace the old log
        if (lastLogged && trimmed.startsWith(lastLogged)) {
          try {
            const logsKey = "sa_no_result_logs";
            const logs = JSON.parse(localStorage.getItem(logsKey) || "[]");
            const lastIndex = [...logs].reverse().findIndex(
              (log: any) => log.searchType === searchType && log.searchData === lastLogged
            );
            if (lastIndex !== -1) {
              const targetIndex = logs.length - 1 - lastIndex;
              logs.splice(targetIndex, 1);
              localStorage.setItem(logsKey, JSON.stringify(logs));
            }
          } catch (err) {
            console.error("Error updating logs:", err);
          }
        } else if (lastLogged && lastLogged.startsWith(trimmed)) {
          // Backspace skip: if user is deleting characters, don't log
          return;
        }

        queueAnalytics(searchType, trimmed);
        resolveAnalytics(false);
        lastLoggedRef.current = trimmed;
      }
    }, 800);
  };

  // Helper to get a badge label for suggestion type
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "CATEGORY": return "Category";
      case "AUTHOR": return "Author";
      case "LANGUAGE": return "Language";
      case "ACADEMIC": return "Academic";
      case "MERCHANDISE": return "Merchandise";
      case "BOOK": return "Book";
      case "RECORD": return "Record";
      default: return type;
    }
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <input
        type="text"
        placeholder={placeholderText}
        className="w-full bg-vintageBg text-black border border-gray-700 border-opacity-50 rounded-xl pl-3 pr-10 py-2 text-xs placeholder:text-gray-700 placeholder:text-[8.5px] min-[360px]:placeholder:text-[9px] min-[400px]:placeholder:text-[10px] min-[480px]:placeholder:text-xs sm:pl-4 sm:pr-12 sm:text-sm sm:placeholder:text-sm lg:pl-5 lg:pr-12 lg:py-3 lg:text-base focus:outline-none focus:ring-0"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue.trim()) setShowSuggestions(true);
        }}
      />
      <div
        className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
        onClick={handleSubmit}
        tabIndex={0}
        role="button"
        aria-label="Search"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleSubmit();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-search"
        >
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      </div>

      {/* Auto-suggestion Dropdown */}
      {showSuggestions && uiState !== "idle" && (
        <div className="absolute top-full left-0 w-full mt-1 bg-vintageBg border border-gray-300 rounded-xl shadow-xl z-50 overflow-hidden">
          {uiState === "loading" && (
            <div className="animate-pulse flex flex-col gap-3 p-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          )}
          {uiState === "error" && (
            <div className="px-4 py-3 text-sm text-red-500 font-medium">
              An error occurred while fetching suggestions.
            </div>
          )}
          {uiState === "empty" && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No results found
            </div>
          )}
          {uiState === "success" && (
            <ul className="max-h-[320px] overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <li
                  key={`${suggestion.type}-${suggestion.label}`}
                  className={`flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm lg:text-base text-gray-800 font-gilroyRegular font-semibold tracking-wider transition-colors ${
                    index === activeIndex
                      ? "bg-vintageText bg-opacity-10"
                      : "hover:bg-vintageText hover:bg-opacity-5"
                  }`}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="truncate">{suggestion.label}</span>
                  <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {getTypeLabel(suggestion.type)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
